import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

export const UNLINK_COMPLETED_EVENT = "unlink_completed";

/** The action simb-pulse-ms stamps on the status notification of a legacy UI unlink. */
const LEGACY_UNLINK_STATUS_ACTION = "delete";

/**
 * One item of a batch unlink has reached its outcome. Emitted by ms-utils into the `user:<id>`
 * room on behalf of simb-orchestration's unlink notifier, once per `items.unlink` job.
 */
export interface UnlinkCompletedEvent {
  userId: string;
  accountId?: string;
  requestId: string;
  itemId: string;
  status: "done" | "dead" | "not_synced";
  error: string | null;
  summary?: { total: number; done: number; dead: number; notSynced: number };
  message: string;
}

interface LegacyUnlinkStatusMessage {
  userId?: string | number;
  action?: string;
  message?: string;
}

interface UseUnlinkStatusStreamArgs {
  userId: string | number | null | undefined;
  accountId?: string | number | null;
  /** Orchestrated path: an outcome for a named item of a named request. */
  onItemResult?: (event: UnlinkCompletedEvent) => void;
  // Room emits are not buffered: an outcome fired while the socket was down is lost, so the
  // consumer reconciles anything still pending here.
  onReconnect?: () => void;
  /** Legacy path: an unlink succeeded. Carries no item id. */
  onUnlinked: () => void;
  /** Legacy path: an unlink failed. Carries no item id. */
  onFailed: (message: string) => void;
}

/**
 * Subscribes to the ms-utils notification socket and forwards the outcome of each queued
 * unlink addressed to this user, on both paths the batch view can take.
 *
 * Deliberately not useErrorWebsocket: that hook mounts useItemLocations and refetches it on
 * every success, which for a batch means one aggregated item-locations query (and one toast)
 * per unlinked item.
 */
const useUnlinkStatusStream = ({
  userId,
  accountId,
  onItemResult,
  onReconnect,
  onUnlinked,
  onFailed,
}: UseUnlinkStatusStreamArgs) => {
  // Keep latest callbacks in refs so the socket effect does not reconnect on every render.
  const onItemResultRef = useRef(onItemResult);
  const onReconnectRef = useRef(onReconnect);
  const onUnlinkedRef = useRef(onUnlinked);
  const onFailedRef = useRef(onFailed);
  onItemResultRef.current = onItemResult;
  onReconnectRef.current = onReconnect;
  onUnlinkedRef.current = onUnlinked;
  onFailedRef.current = onFailed;

  useEffect(() => {
    if (!userId) return;

    const url = `${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`;
    const socket: Socket = io(url);

    // The server only emits to this user's room; the guard is defense in depth against a
    // misrouted emit settling another user's click.
    const isForThisUser = (incomingUserId: string | number | undefined): boolean =>
      String(incomingUserId) === String(userId);

    const isLegacyUnlinkStatus = (data: LegacyUnlinkStatusMessage): boolean =>
      isForThisUser(data?.userId) && data?.action === LEGACY_UNLINK_STATUS_ACTION;

    socket.on(UNLINK_COMPLETED_EVENT, (data: UnlinkCompletedEvent) => {
      if (isForThisUser(data?.userId)) onItemResultRef.current?.(data);
    });

    socket.on("success", (data: LegacyUnlinkStatusMessage) => {
      if (isLegacyUnlinkStatus(data)) onUnlinkedRef.current();
    });

    socket.on("error", (data: LegacyUnlinkStatusMessage) => {
      if (isLegacyUnlinkStatus(data)) onFailedRef.current(data?.message || "Unknown error");
    });

    socket.io.on("reconnect", () => {
      onReconnectRef.current?.();
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId]);
};

export default useUnlinkStatusStream;
